import type { ComponentProps, MouseEventHandler, ReactNode } from 'react';

import { FormLabel } from '@playbook/atoms/form-label';
import { type Size, cn } from '@playbook/utils';

import css from './styles.module.css';

export type TextInputSize = Extract<Size, 'sm' | 'md'>;

const DefaultTextInputSize: TextInputSize = 'md' as const;

export type TextInputStatus = 'error' | 'success';

/**
 *	The text-like input types. Each one keeps the box exactly as drawn — a type
 *	the browser furnishes itself, such as `search` or `date`, renders its own
 *	controls inside it and is not one of these. `number` is, with its spin
 *	buttons hidden in the stylesheet.
 */
export type TextInputType =
	| 'text'
	| 'email'
	| 'password'
	| 'tel'
	| 'url'
	| 'number';

const DefaultTextInputType: TextInputType = 'text' as const;

/**
 *	A field is a textbox, which is what a form wants. With a list of
 *	suggestions it owns — a search, a mention picker — it is a combobox
 *	instead, the one role that makes `aria-activedescendant` and
 *	`aria-expanded` mean something on it. The parent that renders the list is
 *	the one that asks for it, as `atoms/menu-item`'s option is asked for.
 */
export type TextInputRole = 'combobox';

type TextInputEventHandlers = {
	[K in keyof ComponentProps<'input'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'input'>[K];
};

interface TextInputDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

/**
 *	ARIA is admitted the way `data-*` is, on the argument `atoms/button` makes:
 *	a field shown without its header, as a chat's is, still needs a name, and
 *	only whatever places it knows what that is.
 */
interface TextInputAriaAttributes {
	[key: `aria-${string}`]: string | number | boolean | undefined;
}

/**
 *	Figma's `.input-button`: a glyph-only button inside the box, after
 *	everything else in it. A button with no words on screen needs its name, so
 *	the glyph comes with its label or not at all.
 */
type TextInputButton =
	| {
			inputButtonIcon: ReactNode;
			inputButtonLabel: string;
			/** `submit` sends the form the field sits in, as a chat's send does. */
			inputButtonType?: 'button' | 'submit';
			onInputButton?: MouseEventHandler<HTMLButtonElement>;
	  }
	| {
			inputButtonIcon?: undefined;
			inputButtonLabel?: undefined;
			inputButtonType?: undefined;
			onInputButton?: undefined;
	  };

type TextInputProperties = TextInputEventHandlers &
	TextInputDataAttributes &
	TextInputAriaAttributes &
	TextInputButton & {
		size?: TextInputSize;
		status?: TextInputStatus;
		type?: TextInputType;
		disabled?: boolean;
		required?: boolean;
		readOnly?: boolean;
		optional?: boolean;
		/**
		 *	Rounds the box into a pill. Figma has no axis for it: the chat bar
		 *	overrides its instance's radius to 999, and this is that override.
		 */
		rounded?: boolean;
		/**
		 *	The word Apple's keyboards predict inline after the cursor, which
		 *	WebKit calls a writing suggestion. Leave it out and the browser
		 *	decides.
		 */
		writingSuggestions?: boolean;
		/**
		 *	Focuses the field when it is drawn, or, inside a modal, when the modal
		 *	opens.
		 */
		autoFocus?: boolean;
		role?: TextInputRole;

		/**
		 *	`name` is written to the element's `id` as well, so the header points at
		 *	it with the same string and the description and message ids hang off it.
		 */
		name: string;
		value?: string;
		defaultValue?: string;
		placeholder?: string;
		maxLength?: number;
		prefixLabel?: string;
		suffixLabel?: string;

		label?: string;
		description?: string;
		help?: string;
		message?: string;

		leadingIcon?: ReactNode;
		trailingIcon?: ReactNode;
	};

export function TextInput({
	size = DefaultTextInputSize,
	status,
	type = DefaultTextInputType,
	disabled = false,
	required = false,
	readOnly = false,
	optional = false,
	rounded = false,
	writingSuggestions,
	autoFocus = false,
	role,

	name,
	value,
	defaultValue,
	placeholder,
	maxLength,
	prefixLabel,
	suffixLabel,

	label,
	description,
	help,
	message,

	leadingIcon,
	trailingIcon,

	inputButtonIcon,
	inputButtonLabel,
	inputButtonType = 'button',
	onInputButton,

	'aria-describedby': callerDescribedBy,

	...props
}: TextInputProperties): ReactNode {
	const className: string = cn(
		css.field,
		css[size],
		status && css[status],
		rounded && css.rounded
	);

	/**
	 *	What the field is described by, in reading order. A caller's own ids come
	 *	first and are joined rather than replaced. Then the one `atoms/form-label`
	 *	gives its description, and the message below, which is the only place a
	 *	failure is explained in words.
	 */
	const describedBy: string | undefined =
		[
			typeof callerDescribedBy === 'string' && callerDescribedBy,
			description && `${name}-description`,
			message && `${name}-message`,
		]
			.filter(Boolean)
			.join(' ') || undefined;

	/**
	 *	React has no name for `writingsuggestions` yet, so it goes on as the
	 *	plain attribute, and only when it is set.
	 */
	const writingSuggestionsAttribute: Record<string, string> =
		writingSuggestions === undefined
			? {}
			: { writingsuggestions: String(writingSuggestions) };

	/**
	 *	React never writes `autofocus` onto the element: it calls `focus()` once
	 *	the field is mounted, which does nothing in a `<dialog>` that has not
	 *	opened yet, as `modules/modal`'s has not when its views mount. The
	 *	attribute is what the dialog looks for when it opens, so it is written
	 *	here as well, in a ref callback, which runs before the modal opens.
	 */
	const markAutoFocus = (element: HTMLInputElement | null): void => {
		element?.toggleAttribute('autofocus', autoFocus);
	};

	return (
		<div className={className}>
			{label && (
				<FormLabel
					size={size}
					optional={optional}
					required={required}
					htmlFor={name}
					label={label}
					description={description}
					help={help}
				/>
			)}
			<div
				data-size={size}
				data-status={status}
				className={cn(css.input, css[size], status && css[status])}>
				{prefixLabel && <span className={css.affix}>{prefixLabel}</span>}
				{leadingIcon}
				<input
					{...props}
					{...writingSuggestionsAttribute}
					ref={markAutoFocus}
					autoFocus={autoFocus}
					type={type}
					role={role}
					id={name}
					name={name}
					value={value}
					defaultValue={defaultValue}
					placeholder={placeholder}
					maxLength={maxLength}
					disabled={disabled}
					required={required}
					readOnly={readOnly}
					aria-describedby={describedBy}
					aria-invalid={status === 'error' || undefined}
					className={css.control}
				/>
				{trailingIcon}
				{suffixLabel && <span className={css.affix}>{suffixLabel}</span>}
				{inputButtonIcon && (
					<button
						type={inputButtonType}
						aria-label={inputButtonLabel}
						disabled={disabled}
						onClick={onInputButton}
						className={css.inputButton}>
						{inputButtonIcon}
					</button>
				)}
			</div>
			{message && (
				<span id={`${name}-message`} className={css.message}>
					{message}
				</span>
			)}
		</div>
	);
}
